/**
 * TIP Shell — the NAME OVERLAY: trusted-chrome identity painted OVER the app's anchor.
 *
 * THE CONTRACT (Rev 0.3-B, Option B; provisional disclosure [0021]). At high_assurance an
 * app never holds a name. It reserves a fixed-width, empty anchor ("Hi, ⎽⎽⎽⎽.") and calls
 * `tip.identity.renderName(tag, slotId)`. The Shell resolves the name and paints it in the
 * PARENT document. The app POSITIONS that unit (its anchor decides where) but cannot
 * interleave, overlay, reorder, or rebind it: it lives in a document the frame cannot reach.
 *
 * Until 2026-09-29 production built only half of that: the name was painted in a strip
 * above or below the frame, and the greeting inside the frame stayed blank. This module is
 * the geometry half. The SDK reports where the anchor is (`tip:slotRect`, frame-viewport
 * px); the Bridge validates it; this paints the name in a parent-DOM layer laid exactly
 * over the iframe, at that rect.
 *
 * WHY EACH RULE (the frame is UNTRUSTED, so its rect is hostile input):
 *   • CLAMPED to the iframe's own box, and the layer that holds the names is itself the
 *     iframe's box with overflow:hidden — so a hostile rect (negative, huge, off-frame)
 *     cannot paint over Shell UI. It can move the name around INSIDE its own frame, which
 *     it could already do by moving its anchor.
 *   • HEIGHT CAPPED (64px) and never wider than the frame — a slot cannot be turned into a
 *     full-frame name banner.
 *   • SIZED TO THE SLOT, never to the name. A long name is ELLIPSIZED, not grown. The
 *     overlay is absolutely positioned in parent DOM, so painting it changes no layout.
 *     That is only HALF of "identity slots are fixed-width (no reflow leaks name length
 *     back to the frame)": the other half is that the FRAME'S OWN SIZE never depends on
 *     any name-bearing chrome. Until 2026-09-29 (adversarial review, P0) it did — the
 *     student bar wrapped and the frame was flex:1, so a frame could read a classmate's
 *     name length off its own innerHeight. The frame is now sized from CSS constants and
 *     the viewport only (index.html, `.app-view`), and shell/name-leak.browser.test.ts
 *     proves innerWidth/innerHeight identical across names, chip counts and paint state.
 *   • pointer-events:none on the layer AND every chip. A click on the painted name passes
 *     through to the app underneath. ACCEPTED RESIDUAL (threat table, adopted
 *     architecture): the app placed the anchor, so a click there tells it nothing it did
 *     not already know. The alternative was tried and was worse (adversarial review round
 *     2, 2026-09-29): a chip that swallowed clicks existed only once the name RESOLVED, so
 *     whether a click reached the frame — and whether the frame kept keyboard focus — told
 *     the app whether and when the name resolved, and tapping your own name took focus
 *     away from a keyboard game. Nothing the Shell paints may change what input the frame
 *     receives. Pinned by name-leak.browser.test.ts (FOCUS).
 *   • Text via textContent only. The name came from the registrar, the rect from the frame;
 *     neither is ever interpolated into markup.
 *   • Non-finite or absurd numbers are DROPPED, not coerced (fail-closed).
 *
 * Framework-agnostic and imperative on purpose, like the sealed iframe itself (dom.ts):
 * it sits beside the frame, outside any Preact-controlled subtree.
 */
import type { SlotRect } from './protocol.js';

/** Tallest a painted name may be. A name line, not a banner. */
export const MAX_SLOT_H = 64;
/** Smaller than this (after clamping) is unreadable — paint nothing. */
export const MIN_SLOT_PX = 8;
/** Anything beyond this magnitude is not a layout, it is an attack or a bug. */
export const MAX_ABS_COORD = 100_000;
/** How many name slots one frame may have overlaid. */
export const MAX_OVERLAY_SLOTS = 8;

/**
 * Parse a frame-supplied rect.
 *   `null`      — the frame said the anchor is gone (a valid message).
 *   `undefined` — malformed: drop the whole message.
 * Every field must be a finite number of sane magnitude. No coercion: "12" is not 12.
 */
export function parseSlotRect(raw: unknown): SlotRect | null | undefined {
  if (raw === null) return null;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const out: SlotRect = { x: 0, y: 0, w: 0, h: 0 };
  for (const k of ['x', 'y', 'w', 'h'] as const) {
    const v = r[k];
    if (typeof v !== 'number' || !Number.isFinite(v) || Math.abs(v) > MAX_ABS_COORD) return undefined;
    out[k] = v;
  }
  return out;
}

/**
 * Clamp a slot rect to the iframe's box (0,0 → frameW,frameH). Returns what may be
 * painted, or null when nothing readable remains. The result is ALWAYS inside the box.
 */
export function clampSlotRect(r: SlotRect, frameW: number, frameH: number): SlotRect | null {
  if (!(Number.isFinite(frameW) && frameW > 0 && Number.isFinite(frameH) && frameH > 0)) return null;
  if (![r.x, r.y, r.w, r.h].every(Number.isFinite)) return null;
  if (!(r.w > 0) || !(r.h > 0)) return null;
  const left = Math.max(0, r.x);
  const top = Math.max(0, r.y);
  const right = Math.min(frameW, r.x + r.w);
  const bottom = Math.min(frameH, r.y + r.h, top + MAX_SLOT_H);
  const w = right - left;
  const h = bottom - top;
  if (!(w >= MIN_SLOT_PX) || !(h >= MIN_SLOT_PX)) return null;
  return { x: left, y: top, w, h };
}

/** The iframe's content box, in the coordinate space of its parent element. */
export interface FrameBox { left: number; top: number; width: number; height: number }

export interface NameOverlayOptions {
  iframe: HTMLIFrameElement;
  /**
   * Hide the overlay from assistive technology. The student view keeps the strip chip,
   * which is the accessible copy of the name; reading it twice would be noise. The test
   * run has no strip once the overlay is positioned, so there the overlay is what speaks.
   */
  ariaHidden?: boolean;
  /** Measure the iframe's box. Injectable for tests (jsdom has no layout). */
  frameBox?: () => FrameBox | null;
  /** Fired whenever a slot's chip appears or disappears (a rect arrives, goes null, is
   *  clamped away, or the frame resizes). The test run uses it to show its strip exactly
   *  while no chip is painted, so the name is never shown NOWHERE. */
  onPaintedChange?: (slotId: string, painted: boolean) => void;
}

function defaultFrameBox(iframe: HTMLIFrameElement): FrameBox | null {
  const p = iframe.parentElement;
  if (!p || !iframe.isConnected) return null;
  const fr = iframe.getBoundingClientRect();
  const pr = p.getBoundingClientRect();
  return {
    left: fr.left - pr.left - p.clientLeft + p.scrollLeft + iframe.clientLeft,
    top: fr.top - pr.top - p.clientTop + p.scrollTop + iframe.clientTop,
    width: iframe.clientWidth,
    height: iframe.clientHeight,
  };
}

function px(n: number): string {
  return `${Math.round(n * 100) / 100}px`;
}

export class NameOverlay {
  private readonly iframe: HTMLIFrameElement;
  private readonly ariaHidden: boolean;
  private readonly measure: () => FrameBox | null;
  private readonly names = new Map<string, string>();
  private readonly rects = new Map<string, SlotRect | null>();
  private readonly onPaintedChange: ((slotId: string, painted: boolean) => void) | undefined;
  private readonly chips = new Map<string, HTMLElement>();
  private layer: HTMLDivElement | null = null;
  private ro: ResizeObserver | null = null;
  private disposed = false;
  private readonly onResize = (): void => this.relayout();

  constructor(o: NameOverlayOptions) {
    this.iframe = o.iframe;
    this.ariaHidden = o.ariaHidden === true;
    this.measure = o.frameBox ?? (() => defaultFrameBox(o.iframe));
    this.onPaintedChange = o.onPaintedChange;
  }

  /** The resolved name for a slot. Painted only once the slot also has geometry. */
  setName(slotId: string, name: string): void {
    if (this.disposed || !this.admit(slotId)) return;
    this.names.set(slotId, name);
    this.relayout();
  }

  /**
   * The anchor's rect (already validated by the Bridge), or null = the anchor is gone.
   * Re-clamped here against the CURRENT frame box on every layout.
   */
  setRect(slotId: string, rect: SlotRect | null): void {
    if (this.disposed || !this.admit(slotId)) return;
    this.rects.set(slotId, rect);
    this.relayout();
  }

  /** True while a chip for this slot is actually on screen over the frame — NOW, not ever. */
  isPainted(slotId: string): boolean {
    return this.chips.has(slotId);
  }

  /** Re-measure the iframe and re-place every chip (frame resized, moved, went full screen). */
  relayout(): void {
    if (this.disposed) return;
    const box = this.measure();
    if (!box || !this.attach(box)) {
      for (const [slotId, el] of [...this.chips]) {
        el.remove();
        this.chips.delete(slotId);
        this.onPaintedChange?.(slotId, false);
      }
      return;
    }
    const slots = new Set<string>([...this.names.keys(), ...this.rects.keys()]);
    for (const slotId of slots) this.place(slotId, box);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.ro?.disconnect();
    this.ro = null;
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.onResize);
    this.layer?.remove();
    this.layer = null;
    this.chips.clear();
  }

  private admit(slotId: string): boolean {
    return this.names.has(slotId) || this.rects.has(slotId) || this.slotCount() < MAX_OVERLAY_SLOTS;
  }

  private slotCount(): number {
    return new Set<string>([...this.names.keys(), ...this.rects.keys()]).size;
  }

  /** Create (once) the layer that sits exactly over the iframe, and size it to the box. */
  private attach(box: FrameBox): boolean {
    const parent = this.iframe.parentElement;
    if (!parent) return false;
    const doc = this.iframe.ownerDocument;
    if (!this.layer || this.layer.parentElement !== parent) {
      this.layer?.remove();
      const layer = doc.createElement('div');
      layer.className = 'tip-name-layer';
      layer.setAttribute('data-tip-name-layer', '');
      if (this.ariaHidden) layer.setAttribute('aria-hidden', 'true');
      const ls = layer.style;
      ls.position = 'absolute';
      ls.overflow = 'hidden'; // the second wall: nothing inside can paint past the frame
      ls.pointerEvents = 'none';
      ls.zIndex = '1';
      ls.margin = '0';
      ls.padding = '0';
      ls.border = '0';
      // The layer's containing block must be the iframe's parent, and its z-index must
      // stay LOCAL to it — so the overlay can sit above the frame without ever rising
      // above Shell UI that the frame itself sits beneath (a sticky header, a popover).
      const view = doc.defaultView;
      const cs = view ? view.getComputedStyle(parent) : null;
      if (!cs || cs.position === 'static' || cs.position === '') parent.style.position = 'relative';
      parent.style.isolation = 'isolate';
      // Insert right after the iframe so it paints above it in the same stacking context.
      this.iframe.after(layer);
      this.layer = layer;
      for (const slotId of [...this.chips.keys()]) {
        this.chips.delete(slotId);
        this.onPaintedChange?.(slotId, false);
      }
      if (!this.ro && typeof ResizeObserver !== 'undefined') {
        this.ro = new ResizeObserver(() => this.relayout());
        this.ro.observe(this.iframe);
        this.ro.observe(parent);
        if (typeof window !== 'undefined') window.addEventListener('resize', this.onResize);
      }
    }
    const ls = this.layer.style;
    ls.left = px(box.left);
    ls.top = px(box.top);
    ls.width = px(Math.max(0, box.width));
    ls.height = px(Math.max(0, box.height));
    return true;
  }

  private place(slotId: string, box: FrameBox): void {
    const name = this.names.get(slotId);
    const rect = this.rects.get(slotId);
    const clamped = name != null && rect ? clampSlotRect(rect, box.width, box.height) : null;
    let chip = this.chips.get(slotId);
    if (!clamped || name == null || !this.layer) {
      if (chip) {
        chip.remove();
        this.chips.delete(slotId);
        this.onPaintedChange?.(slotId, false);
      }
      return;
    }
    const created = !chip;
    if (!chip) {
      chip = this.iframe.ownerDocument.createElement('span');
      chip.className = 'tip-name-overlay';
      chip.setAttribute('data-tip-slot', slotId); // attribute value, never markup
      const cs = chip.style;
      cs.position = 'absolute';
      cs.boxSizing = 'border-box';
      cs.display = 'block';
      // NEVER an input target (see the header): what the frame receives must not depend on
      // whether a name was painted.
      cs.pointerEvents = 'none';
      cs.overflow = 'hidden';
      cs.whiteSpace = 'nowrap';
      cs.textOverflow = 'ellipsis';
      cs.margin = '0';
      this.layer.appendChild(chip);
      this.chips.set(slotId, chip);
    }
    const cs = chip.style;
    cs.left = px(clamped.x);
    cs.top = px(clamped.y);
    // width/height AND max-* — the box is the slot's, whatever the text wants.
    cs.width = px(clamped.w);
    cs.maxWidth = px(clamped.w);
    cs.height = px(clamped.h);
    cs.maxHeight = px(clamped.h);
    cs.lineHeight = px(clamped.h);
    cs.fontSize = px(Math.max(10, Math.min(22, Math.round(clamped.h * 0.8))));
    if (chip.textContent !== name) chip.textContent = name; // text, never innerHTML
    if (created) this.onPaintedChange?.(slotId, true);
  }
}
